import {
  BlocksProvider,
  LayersProvider,
  PagesProvider,
  SelectorsProvider,
  StylesProvider,
  TraitsProvider,
  useEditorMaybe,
} from '@grapesjs/react';
import { Card } from '@mui/material';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import { RiBook2Line, RiGridLine, RiPaintBrushLine, RiSettings3Line, RiStackLine } from '@remixicon/react';
import { useState } from 'react';

import CustomBlockManager from './CustomBlockManager';
import CustomLayerManager from './CustomLayerManager';
import CustomPageManager from './CustomPageManager';
import CustomSelectorManager from './CustomSelectorManager';
import CustomStyleManager from './CustomStyleManager';
import CustomTraitManager from './CustomTraitManager';
import { cx, MAIN_BORDER_COLOR } from './common';

const defaultTabProps = { className: 'min-w-0!' };

export default function RightSidebar({ className }: React.HTMLAttributes<HTMLDivElement>) {
  const editor = useEditorMaybe();
  const [selectedTab, setSelectedTab] = useState(0);

  return (
    <Card sx={{ overflowY: 'scroll' }}>
      <div className={cx('gjs-right-sidebar flex flex-col', className)}>
        <Tabs
          value={selectedTab}
          onChange={(_, v) => {
            if (v === 0) {
              editor?.runCommand('open-style-manager');
            }

            setSelectedTab(v);
          }}
          variant="fullWidth"
        >
          <Tab {...defaultTabProps} label={<RiPaintBrushLine size={20} />} />
          <Tab {...defaultTabProps} label={<RiSettings3Line size={20} />} />
          <Tab {...defaultTabProps} label={<RiStackLine size={20} />} />
          <Tab {...defaultTabProps} label={<RiGridLine size={20} />} />
          <Tab {...defaultTabProps} label={<RiBook2Line size={20} />} />
        </Tabs>
        <div className={cx('overflow-y-auto grow border-t max-h-full', MAIN_BORDER_COLOR)}>
          <WrappedProviders
            providers={{
              styles: StylesProvider,
              selectors: SelectorsProvider,
              traits: TraitsProvider,
              layers: LayersProvider,
              blocks: BlocksProvider,
              pages: PagesProvider,
            }}
          >
            {(props) => {
              return (
                <>
                  {selectedTab === 0 && (
                    <>
                      <CustomSelectorManager {...props.selectors} />
                      <CustomStyleManager {...props.styles} />
                    </>
                  )}
                  {selectedTab === 1 && <CustomTraitManager {...props.traits} />}
                  {selectedTab === 2 && <CustomLayerManager {...props.layers} />}
                  {selectedTab === 3 && <CustomBlockManager {...props.blocks} />}
                  {selectedTab === 4 && <CustomPageManager {...props.pages} />}
                </>
              );
            }}
          </WrappedProviders>
        </div>
      </div>
    </Card>
  );
}

const WrappedProviders = ({
  providers,
  children,
}: {
  providers: { [key: string]: React.ComponentType<{ children: (value: any) => React.ReactNode }> };
  children: (props: Record<string, any>) => React.ReactNode;
}) => {
  return Object.entries(providers).reduceRight(
    (acc, [mapPropTo, Provider]) =>
      (props) => <Provider>{(value) => acc({ ...props, [mapPropTo]: value })}</Provider>,
    children
  )({});
};
